import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  PeopleService,
  PersonSummary,
  PersonDetails,
  Task,
  Asset,
  PersonActivityLog,
  PersonNote,
  PeopleFilterParams,
  TaskFilterParams,
  CreateTaskRequest,
  UpdateTaskRequest,
  CreatePersonNoteRequest,
  UpdatePersonNoteRequest,
  AxiosErrorResponse,
  PaginationMeta,
} from "@/lib/api/services/peopleService";

export interface PeopleState {
  people: PersonSummary[];
  peoplePagination?: PaginationMeta;

  currentPerson: PersonDetails | null;

  personAssets: Asset[];
  personAssetsPagination?: PaginationMeta;

  personTasks: Task[];
  personTasksPagination?: PaginationMeta;

  personActivityLogs: PersonActivityLog[];
  personActivityLogsPagination?: PaginationMeta;

  personNotes: PersonNote[];
  personNotesPagination?: PaginationMeta;

  isLoading: boolean;
  error: string | null;
  selectedOrganizationId: number | null;
  selectedSiteId: number | null;
  selectedPersonId: number | null;
  searchTerm?: string;
}

const defaultInitialState: PeopleState = {
  people: [],
  peoplePagination: undefined,
  currentPerson: null,
  personAssets: [],
  personAssetsPagination: undefined,
  personTasks: [],
  personTasksPagination: undefined,
  personActivityLogs: [],
  personActivityLogsPagination: undefined,
  personNotes: [],
  personNotesPagination: undefined,
  isLoading: false,
  error: null,
  selectedOrganizationId: null,
  selectedSiteId: null,
  selectedPersonId: null,
  searchTerm: undefined,
};

const initialState: PeopleState = defaultInitialState;

const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosErrorResponse;
  if (axiosError.response?.data) {
    const data = axiosError.response.data;
    if (Array.isArray(data.message)) {
      return data.message[0];
    }
    return data.message || "An unexpected error occurred";
  }
  return (axiosError.message as string) || "An unexpected error occurred";
};

/**
 * Get all people in a site with summary information
 */
export const getPeopleSummary = createAsyncThunk(
  "people/getPeopleSummary",
  async (
    {
      organizationId,
      siteId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      params?: PeopleFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.getPeopleSummary(
        organizationId,
        siteId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get detailed information for a specific person
 */
export const getPersonDetails = createAsyncThunk(
  "people/getPersonDetails",
  async (
    {
      organizationId,
      siteId,
      personId,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await PeopleService.getPersonDetails(
        organizationId,
        siteId,
        personId,
      );

      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get all assets assigned to a person
 */
export const getPersonAssets = createAsyncThunk(
  "people/getPersonAssets",
  async (
    {
      organizationId,
      siteId,
      personId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      params?: PeopleFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.getPersonAssets(
        organizationId,
        siteId,
        personId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get all tasks assigned to a person
 */
export const getPersonTasks = createAsyncThunk(
  "people/getPersonTasks",
  async (
    {
      organizationId,
      siteId,
      personId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      params?: TaskFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.getPersonTasks(
        organizationId,
        siteId,
        personId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Create a new task for a person
 */
export const createTask = createAsyncThunk(
  "people/createTask",
  async (
    {
      organizationId,
      siteId,
      personId,
      request,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      request: CreateTaskRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.createTask(
        organizationId,
        siteId,
        personId,
        request,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Update an existing task
 */
export const updateTask = createAsyncThunk(
  "people/updateTask",
  async (
    {
      organizationId,
      siteId,
      personId,
      taskId,
      request,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      taskId: number;
      request: UpdateTaskRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.updateTask(
        organizationId,
        siteId,
        personId,
        taskId,
        request,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Mark a task as complete
 */
export const completeTask = createAsyncThunk(
  "people/completeTask",
  async (
    {
      organizationId,
      siteId,
      personId,
      taskId,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      taskId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.completeTask(
        organizationId,
        siteId,
        personId,
        taskId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get activity logs for a person
 */
export const getActivityLogs = createAsyncThunk(
  "people/getActivityLogs",
  async (
    {
      organizationId,
      siteId,
      personId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      params?: PeopleFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.getActivityLogs(
        organizationId,
        siteId,
        personId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get notes for a person
 */
export const getPersonNotes = createAsyncThunk(
  "people/getPersonNotes",
  async (
    {
      organizationId,
      siteId,
      personId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      params?: PeopleFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.getPersonNotes(
        organizationId,
        siteId,
        personId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Create a new note for a person
 */
export const createPersonNote = createAsyncThunk(
  "people/createPersonNote",
  async (
    {
      organizationId,
      siteId,
      personId,
      request,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      request: CreatePersonNoteRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.createPersonNote(
        organizationId,
        siteId,
        personId,
        request,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Update a person note
 */
export const updatePersonNote = createAsyncThunk(
  "people/updatePersonNote",
  async (
    {
      organizationId,
      siteId,
      personId,
      noteId,
      request,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      noteId: number;
      request: UpdatePersonNoteRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      return await PeopleService.updatePersonNote(
        organizationId,
        siteId,
        personId,
        noteId,
        request,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Delete a person note
 */
export const deletePersonNote = createAsyncThunk(
  "people/deletePersonNote",
  async (
    {
      organizationId,
      siteId,
      personId,
      noteId,
    }: {
      organizationId: number;
      siteId: number;
      personId: number;
      noteId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      await PeopleService.deletePersonNote(
        organizationId,
        siteId,
        personId,
        noteId,
      );
      return noteId;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

const peopleSlice = createSlice({
  name: "people",
  initialState,
  reducers: {
    setSelectedOrganization: (state, action: PayloadAction<number>) => {
      state.selectedOrganizationId = action.payload;
    },
    setSelectedSite: (state, action: PayloadAction<number>) => {
      state.selectedSiteId = action.payload;
    },
    setSelectedPerson: (state, action: PayloadAction<number>) => {
      state.selectedPersonId = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetPeople: () => {
      return defaultInitialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPeopleSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPeopleSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.people = action.payload.people;
        state.peoplePagination = action.payload.pagination;
      })
      .addCase(getPeopleSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getPersonDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPersonDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPerson = action.payload;
      })
      .addCase(getPersonDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getPersonAssets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPersonAssets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personAssets = action.payload.assets;
        state.personAssetsPagination = action.payload.pagination;
      })
      .addCase(getPersonAssets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getPersonTasks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPersonTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personTasks = action.payload.tasks;
        state.personTasksPagination = action.payload.pagination;
      })
      .addCase(getPersonTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createTask.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personTasks.unshift(action.payload);
      })
      .addCase(createTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateTask.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.personTasks.findIndex(
          (t) => t.id === action.payload.id,
        );
        if (index !== -1) {
          state.personTasks[index] = action.payload;
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(completeTask.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(completeTask.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.personTasks.findIndex(
          (t) => t.id === action.payload.id,
        );
        if (index !== -1) {
          state.personTasks[index] = action.payload;
        }
      })
      .addCase(completeTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getActivityLogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getActivityLogs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personActivityLogs = action.payload.logs;
        state.personActivityLogsPagination = action.payload.pagination;
      })
      .addCase(getActivityLogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getPersonNotes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPersonNotes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personNotes = action.payload.notes;
        state.personNotesPagination = action.payload.pagination;
      })
      .addCase(getPersonNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createPersonNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPersonNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personNotes.unshift(action.payload);
      })
      .addCase(createPersonNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updatePersonNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePersonNote.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.personNotes.findIndex(
          (n) => n.id === action.payload.id,
        );
        if (index !== -1) {
          state.personNotes[index] = action.payload;
        }
      })
      .addCase(updatePersonNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deletePersonNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePersonNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personNotes = state.personNotes.filter(
          (n) => n.id !== action.payload,
        );
      })
      .addCase(deletePersonNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedOrganization,
  setSelectedSite,
  setSelectedPerson,
  setSearchTerm,
  clearError,
  resetPeople,
} = peopleSlice.actions;

export default peopleSlice.reducer;
